import { createFileRoute, Outlet } from "@tanstack/react-router";
import { z } from "zod";
import type { BrewcontrolNodeId } from "@brewbase/db";
import { useMemo } from "react";
import {
  CircuitBoardIcon,
  GaugeIcon,
  RulerIcon,
  ScrollTextIcon,
  ServerIcon,
  SlidersHorizontalIcon,
} from "lucide-react";
import { trpc } from "@/hooks/trpc";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import type { SidebarNavItem } from "@/components/nav/sidebar-store";
import { ConnectionLabel } from "@/features/brewcontrol/node-status";

export const Route = createFileRoute("/brewcontrol/$nodeId")({
  component: NodeLayout,
  // Brand the id once, here, so every child reads a BrewcontrolNodeId. A
  // type-only import: the value export lives in @brewbase/db's index, which
  // also constructs the pg client and must never reach the browser bundle.
  params: z.object({
    nodeId: z.string() as unknown as z.ZodType<BrewcontrolNodeId>,
  }),
});

/**
 * Node layer. Everything under here is scoped to one physical tank's agent.
 *
 * The entries follow ownership, which is what makes the staleness story
 * legible: Overview and Devices are node-owned state BrewBase caches, Logs is
 * node-owned, and Config (devices, workspaces, wiring, tuning) is BrewBase-owned
 * and pushed down. Recipes are
 * deliberately absent — they are per-run and portable, so they live with the
 * fermentation, not with a machine.
 */
function NodeLayout() {
  const { nodeId } = Route.useParams();
  const { data: node } = trpc.brewcontrol.getNode.useQuery(
    { id: nodeId },
    { refetchInterval: 5000 },
  );

  const navigation = useMemo<SidebarNavItem[]>(
    () => [
      {
        type: "link",
        id: "overview",
        label: "Overview",
        icon: GaugeIcon,
        href: `/brewcontrol/${nodeId}`,
        exact: true,
      },
      {
        type: "link",
        id: "devices",
        label: "Devices",
        icon: CircuitBoardIcon,
        href: `/brewcontrol/${nodeId}/devices`,
      },
      {
        type: "link",
        id: "calibration",
        label: "Calibration",
        icon: RulerIcon,
        href: `/brewcontrol/${nodeId}/calibration`,
      },
      {
        type: "link",
        id: "logs",
        label: "Logs",
        icon: ScrollTextIcon,
        href: `/brewcontrol/${nodeId}/logs`,
      },
      { type: "divider", id: "config-divider" },
      {
        type: "link",
        id: "config",
        label: "Config",
        icon: SlidersHorizontalIcon,
        href: `/brewcontrol/${nodeId}/config`,
      },
    ],
    [nodeId],
  );

  useSidebarConfig({
    layer: 2,
    parent: { label: "Fleet", icon: ServerIcon, href: "/brewcontrol" },
    header: {
      title: node?.display_name ?? "Node",
      subtitle: node?.hostname ?? undefined,
      status: node ? <ConnectionLabel connected={node.connected} /> : undefined,
    },
    navigation,
  });

  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden">
      <main className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
